import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { animate, motion, useReducedMotion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faPen, faUser } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { notebookContext } from "../providers/notebook.provider"
import ContentPill from "../atoms/ContentPill"
import {
	CopyIcon,
	LevelTags,
	NotebookShare,
	SharedBadge,
	VariantNote,
	VariantTabs,
	useCopyToNotebook,
} from "./NotebookVariants"
import { SchemaPreview, SchemaThumbnail } from "../lesson/schema/ExerciseSchema"
import {
	PHASES,
	chosenAreas,
	isPhaseWritten,
	joinPhaseLabels,
	normalizeLessonContent,
	playerStyleLabel,
} from "../utils/lessonContent.utils"
import {
	NOTEBOOK_EXERCISE,
	entryAt,
	exercisePhaseLabel,
	isMine,
	isSharedIn,
	lastUseLabel,
	ownerLabel,
	usesLabel,
	variantOf,
} from "../utils/notebook.utils"

// ── Una voce del quaderno, che si apre sul posto ──
//
// Chiusa è una riga: il nome, l'obiettivo, su cosa si lavora e, sotto,
// quattro pallini sul filo — le quattro fasi del programma, piene quelle
// scritte — e quando la si è usata l'ultima volta. Toccata si apre lì dove
// sta e mostra tutto: i quattro pallini scendono dalla riga e diventano le
// fasi in colonna, ognuna col suo obiettivo e i suoi esercizi. Chi non sa
// cosa siano i pallini lo vede la prima volta che ne apre una; chiudendola
// tornano su in fila.
//
// Si chiude al contrario e tutto dentro la riga: le scritte si spengono, il
// fondo si riavvolge e la riga si accorcia mentre i pallini risalgono. I
// pallini salgono alla stessa velocità con cui sale il fondo della riga, e
// così non escono mai sopra le voci di sotto: era quello che succedeva
// chiudendo di colpo e facendoli volare dopo.
//
// **Modifica**, dentro, apre il foglio di prima.
//
// Accanto al nome, le due sagome se è condivisa e i livelli delle sue
// varianti; di un collega, in fondo alla riga, di chi è ("di Giulia Ferri"). Aperta, in cima ci sono le
// varianti da scegliere, con "+ Variante" per chi l'ha scritta, e la riga
// che dice cosa cambia: tutto quello che si legge, anche da chiusa, è della
// variante scelta. In fondo "Condividi con i colleghi" e Modifica; di un
// collega, di chi è e "Copia nel mio quaderno".

const UNFOLD = { duration: 0.48, ease: [0.2, 0.8, 0.2, 1] }
const FOLD = { duration: 0.44, ease: [0.4, 0, 0.2, 1] }
const GROUND_SHOWN = "inset(0% 0% 0% 0% round 15px)"
const GROUND_HIDDEN = "inset(0% 0% 100% 0% round 15px)"
const DOT = 8
const BIG_DOT = 12

// Dove deve stare un pallino delle fasi per coprire il suo gemello nella
// riga: lo spostamento e quanto rimpicciolire. Si misura dal suo posto vero,
// senza lo spostamento che ha addosso se lo si ferma a metà volo.
const offsetTo = (big, small) => {
	const from = small.getBoundingClientRect()
	const moved = big.style.transform
	big.style.transform = "none"
	const to = big.getBoundingClientRect()
	big.style.transform = moved
	return {
		x: from.left - to.left,
		y: from.top - to.top,
		scale: to.width ? from.width / to.width : 1,
	}
}

// I pallini delle fasi accanto ai loro gemelli nella riga, uno per fase
const dotPairs = row => {
	const small = row.querySelectorAll("[data-dot]")
	return Array.from(row.querySelectorAll("[data-phase-dot]"))
		.map((big, i) => [big, small[i]])
		.filter(([, twin]) => twin)
}

// Si apre: la riga si allunga dall'altezza che aveva, il fondo si srotola,
// i pallini scendono e le scritte arrivano subito dopo
const unfold = (row, from) => {
	const to = row.offsetHeight
	row.style.overflow = "hidden"
	const height = animate(row, { height: [from, to] }, UNFOLD)
	// finita, la riga torna della sua altezza; non se intanto si sta chiudendo
	height.then(() => {
		if (!row.hasAttribute("data-folding")) Object.assign(row.style, { height: "", overflow: "" })
	})
	const controls = [height]

	const ground = row.querySelector("[data-ground]")
	if (ground) controls.push(animate(ground, { clipPath: [GROUND_HIDDEN, GROUND_SHOWN] }, UNFOLD))
	dotPairs(row).forEach(([big, small]) => {
		const { x, y, scale } = offsetTo(big, small)
		controls.push(animate(big, { x: [x, 0], y: [y, 0], scale: [scale, 1] }, UNFOLD))
	})
	row.querySelectorAll("[data-fade]").forEach(element => {
		element.style.opacity = "0"
		controls.push(
			animate(
				element,
				{ opacity: [0, 1], y: [-4, 0] },
				{ duration: 0.3, delay: 0.24, ease: "easeOut" },
			),
		)
	})
	return controls
}

// Quanto è alta la riga chiusa, senza chiuderla: si nasconde un attimo
// quello che c'è solo da aperta, si ritaglia il testo, si misura e si
// rimette tutto com'era, prima che il browser disegni. Anche l'altezza della
// riga, se la si chiude mentre si sta ancora aprendo.
const foldedHeight = row => {
	const unfolded = row.querySelector("[data-unfolded]")
	const clamped = row.querySelector("[data-clamp]")
	const saved = [row, unfolded, clamped].map(element => element?.style.cssText)
	row.style.height = ""
	if (unfolded) unfolded.style.display = "none"
	if (clamped)
		Object.assign(clamped.style, {
			display: "-webkit-box",
			WebkitLineClamp: "2",
			WebkitBoxOrient: "vertical",
			overflow: "hidden",
		})
	const height = row.offsetHeight
	;[row, unfolded, clamped].forEach((element, i) => element && (element.style.cssText = saved[i]))
	return height
}

// Si chiude: le scritte si spengono, il fondo si riavvolge e la riga si
// accorcia mentre i pallini risalgono in fila
const fold = (row, done) => {
	const from = row.offsetHeight
	const to = foldedHeight(row)
	row.setAttribute("data-folding", "")
	row.style.overflow = "hidden"
	const height = animate(row, { height: [from, to] }, FOLD)
	height.then(() => {
		row.removeAttribute("data-folding")
		done()
	})
	const controls = [height]

	row
		.querySelectorAll("[data-fade]")
		.forEach(element => controls.push(animate(element, { opacity: 0 }, { duration: 0.14 })))
	const ground = row.querySelector("[data-ground]")
	if (ground) controls.push(animate(ground, { clipPath: [GROUND_SHOWN, GROUND_HIDDEN] }, FOLD))
	dotPairs(row).forEach(([big, small]) => {
		const { x, y, scale } = offsetTo(big, small)
		controls.push(animate(big, { x, y, scale }, FOLD))
	})
	return controls
}

// Un pallino di una fase: pieno se la fase è scritta, un cerchio se no.
// `ground` è il fondo su cui sta, che riempie il cerchio e lo stacca dal filo.
function PhaseDot({ written, size, ground, ...others }) {
	return (
		<span
			{...others}
			style={{
				position: "relative",
				flex: "none",
				width: size,
				height: size,
				borderRadius: "50%",
				transformOrigin: "0 0",
				background: written ? theme.colors.primary : ground,
				boxShadow: written ? "none" : `inset 0 0 0 1.5px ${theme.colors.gunmetal_light}`,
			}}
		/>
	)
}

// Le quattro fasi in fila sul filo, nella riga chiusa
function PhaseDots({ phases, hidden }) {
	const written = phases.filter(phase => phase.isWritten)
	return (
		<span
			role="img"
			aria-label={
				written.length > 0
					? `Fasi scritte: ${joinPhaseLabels(written).toLowerCase()}`
					: "Nessuna fase scritta"
			}
			style={{
				position: "relative",
				display: "flex",
				alignItems: "center",
				gap: "7px",
				flex: "none",
				// da aperta restano al loro posto, invisibili: sono il punto da
				// cui partono e a cui tornano i pallini delle fasi
				visibility: hidden ? "hidden" : "visible",
			}}>
			<span
				aria-hidden="true"
				style={{
					position: "absolute",
					left: DOT / 2,
					right: DOT / 2,
					top: "50%",
					height: "1.5px",
					marginTop: "-0.75px",
					background: theme.colors.gunmetal_lighter,
				}}
			/>
			{phases.map(phase => (
				<PhaseDot
					key={phase.id}
					data-dot=""
					written={phase.isWritten}
					size={DOT}
					ground={theme.colors.surface}
				/>
			))}
		</span>
	)
}

// Le fasi in colonna, nella lezione aperta: ognuna col suo pallino, legate
// dallo stesso filo
function PhaseColumn({ phases }) {
	return (
		<div
			style={{
				position: "relative",
				padding: "14px 14px 14px 12px",
				display: "flex",
				flexDirection: "column",
				gap: "16px",
			}}>
			<span
				data-ground=""
				aria-hidden="true"
				style={{
					position: "absolute",
					inset: 0,
					borderRadius: "15px",
					background: theme.colors.background,
				}}
			/>
			{phases.map((phase, i) => (
				<div
					key={phase.id}
					style={{
						position: "relative",
						display: "grid",
						gridTemplateColumns: `${BIG_DOT}px minmax(0, 1fr)`,
						columnGap: "10px",
						rowGap: "5px",
					}}>
					{i < phases.length - 1 && (
						<span
							data-fade=""
							aria-hidden="true"
							style={{
								position: "absolute",
								left: BIG_DOT / 2 - 0.75,
								top: BIG_DOT,
								bottom: "-16px",
								width: "1.5px",
								background: theme.colors.gunmetal_lighter,
							}}
						/>
					)}
					<PhaseDot
						data-phase-dot=""
						written={phase.isWritten}
						size={BIG_DOT}
						ground={theme.colors.background}
					/>
					<span
						data-fade=""
						style={{
							fontSize: "0.75rem",
							lineHeight: `${BIG_DOT}px`,
							fontWeight: phase.isWritten ? 600 : 500,
							color: phase.isWritten ? theme.colors.gunmetal : theme.colors.text_light,
						}}>
						{phase.label}
						{!phase.isWritten && " · non scritta"}
					</span>
					{phase.objective && (
						<span
							data-fade=""
							style={{
								gridColumn: 2,
								fontSize: "0.8125rem",
								fontStyle: "italic",
								lineHeight: 1.4,
								color: theme.colors.gunmetal,
							}}>
							{phase.objective}
						</span>
					)}
					{phase.exercises.map((exercise, j) => (
						<span
							key={j}
							data-fade=""
							style={{
								gridColumn: 2,
								fontSize: "0.8125rem",
								lineHeight: 1.4,
								color: theme.colors.gunmetal,
								whiteSpace: "pre-line",
							}}>
							{exercise}
						</span>
					))}
				</div>
			))}
		</div>
	)
}

// Di un collega, in fondo alla riga, di chi è. Le proprie condivise hanno
// le due sagome accanto al nome (`SharedBadge`), e le altre niente.
function OwnerMark({ entry, schoolID }) {
	return (
		<span
			style={{
				marginLeft: "auto",
				display: "inline-flex",
				alignItems: "center",
				gap: "0.3rem",
				fontSize: "0.75rem",
				color: theme.colors.text_light,
				whiteSpace: "nowrap",
			}}>
			<FontAwesomeIcon icon={faUser} size="xs" />
			{ownerLabel(entry, schoolID)}
		</span>
	)
}

// Le pastiglie di una lezione: il tipo di giocatore, poi su cosa si lavora
const lessonPills = content => [
	...(content.playerStyle
		? [
				{
					key: content.playerStyle,
					text: playerStyleLabel(content.playerStyle),
					kind: "playerStyle",
				},
			]
		: []),
	...chosenAreas(content).map(({ area, detail }) => ({
		key: area.id,
		text: area.label,
		detail,
		kind: area.list === "situations" ? "situation" : "stroke",
	})),
]

function NotebookRow({ entry: listed, onEdit, onAddVariant, highlightLevel = null, divided = true }) {
	const { schoolID } = useContext(notebookContext)
	const { copy, isCopying } = useCopyToNotebook()
	// la variante che si guarda: quella con cui la riga arriva (la pastiglia
	// di un livello sceglie la sua), finché non se ne tocca un'altra
	const [variantID, setVariantID] = useState(listed.variantID)
	useEffect(() => setVariantID(listed.variantID), [listed.variantID])
	const entry = variantOf(listed, variantID) ? entryAt(listed, variantID) : listed
	const reduceMotion = useReducedMotion()
	const [isOpen, setOpen] = useState(false)
	const [isClosing, setClosing] = useState(false)
	const rowRef = useRef(null)
	const openedFrom = useRef(null)
	const controls = useRef([])

	const isExercise = entry.kind === NOTEBOOK_EXERCISE
	const content = isExercise ? null : normalizeLessonContent(entry.content)
	const pills = content ? lessonPills(content) : []
	const phases = content
		? PHASES.map(phase => ({
				...phase,
				...content.phases[phase.id],
				isWritten: isPhaseWritten(content.phases[phase.id]),
			}))
		: []
	const looksOpen = isOpen && !isClosing

	const stop = () => {
		controls.current.forEach(control => control.stop())
		controls.current = []
	}

	const toggle = () => {
		if (isClosing) return
		if (!isOpen) {
			stop()
			openedFrom.current = rowRef.current?.offsetHeight ?? null
			setOpen(true)
		} else if (reduceMotion) setOpen(false)
		else {
			stop()
			setClosing(true)
		}
	}

	useLayoutEffect(() => {
		const row = rowRef.current
		if (!row) return
		if (!isOpen) {
			// chiusa del tutto: la riga torna della sua altezza
			Object.assign(row.style, { height: "", overflow: "" })
			return
		}
		if (reduceMotion) return
		if (isClosing)
			controls.current = fold(row, () => {
				setOpen(false)
				setClosing(false)
			})
		else if (openedFrom.current !== null) {
			controls.current = unfold(row, openedFrom.current)
			openedFrom.current = null
		}
	}, [isOpen, isClosing, reduceMotion])

	useEffect(() => stop, [])

	return (
		<div
			ref={rowRef}
			style={{
				position: "relative",
				background: theme.colors.surface,
				// il filo del quaderno tra una voce e l'altra
				boxShadow: divided ? `inset 0 1px 0 ${theme.colors.gunmetal_lighter}` : "none",
			}}>
			<button
				type="button"
				aria-expanded={looksOpen}
				onClick={toggle}
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "0.25rem",
					width: "100%",
					border: 0,
					background: "transparent",
					padding: "0.75rem 1rem",
					textAlign: "left",
					color: theme.colors.gunmetal,
					font: "inherit",
					cursor: "pointer",
				}}>
				<span
					style={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: "0.75rem",
						width: "100%",
					}}>
					<span style={{ fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.3 }}>
						{entry.title}
					</span>
					<span style={{ flex: 1 }} />
					{isMine(entry) && isSharedIn(entry, schoolID) && <SharedBadge entry={entry} />}
					<LevelTags entry={entry} highlight={highlightLevel} />
					<motion.span
						aria-hidden="true"
						animate={{ rotate: looksOpen ? 180 : 0 }}
						transition={reduceMotion ? { duration: 0 } : UNFOLD}
						style={{ display: "flex", flex: "none", marginTop: "0.15rem" }}>
						<FontAwesomeIcon icon={faAngleDown} color={theme.colors.text_light} />
					</motion.span>
				</span>

				{isExercise ? (
					<>
						<span style={{ fontSize: "0.75rem", color: theme.colors.text_light }}>
							{exercisePhaseLabel(entry.phase)} · {lastUseLabel(entry)}
							{!isMine(entry) && ` · ${ownerLabel(entry, schoolID)}`}
						</span>
						{/* lo schema, se c'è, accanto al testo: da chiusa è la miniatura,
						    da aperta si vede grande qui sotto */}
						{(entry.text || (entry.schema && !looksOpen)) && (
							<span style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
								<span
									data-clamp=""
									style={{
										flex: 1,
										minWidth: 0,
										fontSize: "0.8125rem",
										lineHeight: 1.4,
										whiteSpace: "pre-line",
										...(isOpen
											? {}
											: {
													display: "-webkit-box",
													WebkitLineClamp: 2,
													WebkitBoxOrient: "vertical",
													overflow: "hidden",
												}),
									}}>
									{entry.text}
								</span>
								{/* chiudendo c'è già, così la riga si misura chiusa per com'è */}
								{entry.schema && !looksOpen && (
									<SchemaThumbnail
										schema={entry.schema}
										width="4.5rem"
										height="5rem"
									/>
								)}
							</span>
						)}
					</>
				) : (
					<>
						{content.objective && (
							<span style={{ fontSize: "0.8125rem", fontStyle: "italic", lineHeight: 1.4 }}>
								{content.objective}
							</span>
						)}
						{pills.length > 0 && (
							<span
								style={{
									display: "flex",
									flexWrap: "wrap",
									gap: "0.3rem",
									marginTop: "0.2rem",
								}}>
								{pills.map(({ key, text, detail, kind }) => (
									<ContentPill
										key={key}
										kind={kind}
										detail={detail}
										style={{
											padding: "0.2rem 0.6rem",
											lineHeight: "1.1rem",
											borderRadius: "12px",
											fontSize: "0.75rem",
										}}>
										{text}
									</ContentPill>
								))}
							</span>
						)}
						<span
							style={{
								display: "flex",
								alignItems: "center",
								gap: "0.625rem",
								marginTop: "0.25rem",
							}}>
							<PhaseDots phases={phases} hidden={isOpen} />
							<span style={{ fontSize: "0.75rem", color: theme.colors.text_light }}>
								{lastUseLabel(entry)}
							</span>
							{!isMine(entry) && <OwnerMark entry={entry} schoolID={schoolID} />}
						</span>
					</>
				)}
			</button>

			{isOpen && (
				<div
					data-unfolded=""
					style={{
						padding: "0 1rem 0.875rem",
						display: "flex",
						flexDirection: "column",
						gap: "0.75rem",
					}}>
					{(entry.variants?.length > 1 || entry.level || isMine(entry)) && (
						<div data-fade="" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
							<VariantTabs
								entry={entry}
								selected={entry.variantID}
								onSelect={setVariantID}
								onAdd={isMine(entry) && onAddVariant ? () => onAddVariant(entry.variantID) : null}
							/>
							<VariantNote entry={entry} variant={entry} />
						</div>
					)}
					{!isExercise && <PhaseColumn phases={phases} />}
					{/* lo schema di un esercizio, da aperto, grande: toccato si apre
					    a tutto schermo */}
					{isExercise && entry.schema && (
						<div data-fade="" style={{ display: "flex", justifyContent: "center" }}>
							<SchemaPreview schema={entry.schema} label={entry.title} large />
						</div>
					)}
					<div data-fade="">
						<NotebookShare entry={entry} />
					</div>
					<div
						data-fade=""
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: "0.75rem",
						}}>
						<span style={{ fontSize: "0.75rem", color: theme.colors.text_light }}>
							{usesLabel(entry)}
						</span>
						{isMine(entry) ? (
							<button
								type="button"
								onClick={() => onEdit(entry.variantID)}
								style={{
									flex: "none",
									display: "flex",
									alignItems: "center",
									gap: "0.5rem",
									height: "2.5rem",
									padding: "0 1rem",
									border: 0,
									borderRadius: "20px",
									background: theme.colors.gunmetal_fill,
									color: theme.colors.white,
									font: "inherit",
									fontSize: "0.875rem",
									fontWeight: 600,
									cursor: "pointer",
								}}>
								<FontAwesomeIcon icon={faPen} size="sm" />
								Modifica
							</button>
						) : (
							<button
								type="button"
								disabled={isCopying}
								onClick={() => copy(entry)}
								style={{
									flex: "none",
									display: "flex",
									alignItems: "center",
									gap: "0.5rem",
									height: "2.5rem",
									padding: "0 1rem",
									border: 0,
									borderRadius: "20px",
									background: "transparent",
									boxShadow: `inset 0 0 0 2px ${theme.colors.gunmetal}`,
									color: theme.colors.gunmetal,
									font: "inherit",
									fontSize: "0.875rem",
									fontWeight: 600,
									cursor: "pointer",
								}}>
								<CopyIcon />
								{isCopying ? "Copio..." : "Copia nel mio quaderno"}
							</button>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

export default NotebookRow
